import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    HStack,
    IconButton,
    Spinner,
    Table,
    Text,
    VStack,
} from "@chakra-ui/react";
import { HiArrowDownTray, HiEye, HiPaperClip } from "react-icons/hi2";
import dayjs from "dayjs";

const formatFileSize = (bytes) => {
    if (bytes == null) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const CandidateAttachmentsTab = ({ attachments, isLoading, onOpenAttachment, onDownloadAttachment, onUploadAttachments, onDownloadAllZip }) => {
    const { t } = useTranslation("models");
    const [isUploading, setIsUploading] = useState(false);

    const handleFilesSelected = async (e) => {
        const files = Array.from(e.target.files ?? []);
        e.target.value = "";
        if (!files.length) return;
        setIsUploading(true);
        try {
            await onUploadAttachments(files);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Box>
            <HStack justify="flex-end" mb={4}>
                <Button variant="outlined" size="sm" disabled={attachments.length === 0} onClick={onDownloadAllZip}>
                    {t("candidateDetailPage.downloadAllZip")}
                </Button>
                <Button
                    as="label"
                    htmlFor="attachment-file-input"
                    variant="outlined"
                    size="sm"
                    disabled={isLoading || isUploading}
                    cursor="pointer"
                >
                    {isUploading ? <Spinner size="xs" /> : <HiPaperClip />}
                    {t("candidateDetailPage.uploadAttachments")}
                    <input
                        id="attachment-file-input"
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleFilesSelected}
                    />
                </Button>
            </HStack>
            {isLoading ? (
                <VStack py={8} align="center"><Spinner color="brand.400" size="sm" /></VStack>
            ) : attachments.length === 0 ? (
                <VStack py={10} align="center">
                    <Text color="text.grey" fontSize="sm">{t("candidateDetailPage.noAttachments")}</Text>
                </VStack>
            ) : (
                <>
                    <Box display={{ base: 'none', md: 'block' }} border="1px solid" borderColor="border.primary" borderRadius={12} overflowX="auto">
                        <Table.Root minW="720px">
                            <Table.Header>
                                <Table.Row bg="bg.card" borderBottom="1px solid" borderColor="border.primary">
                                    {[
                                        t("candidateDetailPage.columnFileName"),
                                        t("candidateDetailPage.columnKind"),
                                        t("candidateDetailPage.columnAuthor"),
                                        t("candidateDetailPage.columnDate"),
                                        t("candidateDetailPage.columnSize"),
                                        "",
                                    ].map((col, i) => (
                                        <Table.ColumnHeader
                                            key={i}
                                            color="text.grey"
                                            fontWeight={600}
                                            fontSize="xs"
                                            textTransform="uppercase"
                                            letterSpacing="wider"
                                            py={3}
                                            px={5}
                                            border="none"
                                            textAlign={i === 5 ? "right" : "left"}
                                        >
                                            {col}
                                        </Table.ColumnHeader>
                                    ))}
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {attachments.map((att) => (
                                    <Table.Row
                                        key={att.name}
                                        bg="bg.secondary"
                                        borderBottom="1px solid"
                                        borderColor="border.primary"
                                        _hover={{ bg: "bg.highlight" }}
                                        transition="background 0.15s"
                                    >
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="white" fontSize="sm" fontFamily="mono">{att.name}</Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="text.grey" fontSize="xs">{att.kind ?? att.type ?? "-"}</Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="text.grey" fontSize="xs">{att.authorFullName ?? "-"}</Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="text.grey" fontSize="xs">
                                                {att.createdAt ? dayjs(att.createdAt).format("DD.MM.YYYY HH:mm") : "-"}
                                            </Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="text.grey" fontSize="xs">{formatFileSize(att.fileSize)}</Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none" textAlign="right">
                                            {att.path && (
                                                <HStack justify="flex-end" gap={2}>
                                                    <IconButton
                                                        variant="outlined"
                                                        size="sm"
                                                        aria-label={t("candidateDetailPage.openFile")}
                                                        onClick={() => onOpenAttachment(att)}
                                                    >
                                                        <HiEye />
                                                    </IconButton>
                                                    <IconButton
                                                        variant="outlined"
                                                        size="sm"
                                                        aria-label={t("candidateDetailPage.downloadFile")}
                                                        onClick={() => onDownloadAttachment(att)}
                                                    >
                                                        <HiArrowDownTray />
                                                    </IconButton>
                                                </HStack>
                                            )}
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table.Root>
                    </Box>
                    <VStack display={{ base: 'flex', md: 'none' }} gap={0} bg="bg.secondary" border="1px solid" borderColor="border.primary" borderRadius={12} overflow="hidden">
                        {attachments.map((att, i) => (
                            <HStack
                                key={att.name}
                                w="full"
                                justify="space-between"
                                px={4}
                                py={3}
                                borderBottom={i < attachments.length - 1 ? "1px solid" : "none"}
                                borderColor="border.primary"
                            >
                                <VStack align="flex-start" gap={0} flex={1} minW={0}>
                                    <Text color="white" fontSize="sm" fontFamily="mono">{att.name}</Text>
                                    <Text color="text.grey" fontSize="xs">{att.kind ?? att.type ?? "-"}</Text>
                                    {att.authorFullName && (
                                        <Text color="text.grey" fontSize="xs">{att.authorFullName}</Text>
                                    )}
                                </VStack>
                                {att.path && (
                                    <HStack gap={2} flexShrink={0}>
                                        <IconButton
                                            variant="outlined"
                                            size="sm"
                                            aria-label={t("candidateDetailPage.openFile")}
                                            onClick={() => onOpenAttachment(att)}
                                        >
                                            <HiEye />
                                        </IconButton>
                                        <IconButton
                                            variant="outlined"
                                            size="sm"
                                            aria-label={t("candidateDetailPage.downloadFile")}
                                            onClick={() => onDownloadAttachment(att)}
                                        >
                                            <HiArrowDownTray />
                                        </IconButton>
                                    </HStack>
                                )}
                            </HStack>
                        ))}
                    </VStack>
                </>
            )}
        </Box>
    );
};

export default CandidateAttachmentsTab;
